import { Decimal } from 'decimal.js';
import { getDateFromUnix, getUnixTime } from '@einhaus/meteoric-helpers';
const Money = Decimal.clone({ precision: 60 });
const cents = (value) => value.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
const yearAt = (timestamp) => Number(getDateFromUnix(timestamp, 'ymd').slice(0, 4));
const openingTypes = new Set(['buy', 'buyToOpen', 'shortSell', 'sellToOpen']);
const closingTypes = new Set(['sell', 'sellToClose', 'buyToCover', 'buyToClose', 'expiration']);
export function validatePortfolioTaxConfiguration(value) {
    const { federalRatePercent, stateRatePercent } = value;
    if (!Number.isFinite(federalRatePercent) ||
        !Number.isFinite(stateRatePercent) ||
        federalRatePercent < 0 ||
        stateRatePercent < 0 ||
        federalRatePercent + stateRatePercent > 100)
        throw new Error('Federal and state tax rates must be nonnegative and total at most 100%.');
    return { federalRatePercent, stateRatePercent };
}
/** Incremental flat-rate estimate, not a tax-return engine. Tax lots belong to executed portfolio legs.
 * Finalized prior-year tax is reserved from new admissions until its literal April 15 payment.
 * Losses offset capital gains only; unused losses have no assumed cash/refund value.
 */
export class PortfolioTaxAccount {
    lots = new Map();
    years = new Map();
    currentYear;
    realizedGain = new Money(0);
    ordinaryIncome = new Money(0);
    carryforward = new Money(0);
    lastTimestamp;
    configuration;
    constructor(configuration, start) {
        this.configuration = validatePortfolioTaxConfiguration(configuration);
        this.currentYear = yearAt(start);
        this.lastTimestamp = start;
    }
    static validateTransactions(transactions) {
        for (const tx of transactions) {
            if (!Number.isFinite(tx.value) ||
                !Number.isFinite(tx.quantity) ||
                tx.quantity < 0 ||
                !Number.isFinite(tx.fees) ||
                tx.fees < 0 ||
                !Number.isSafeInteger(tx.timestamp))
                throw new Error('Tax transactions require finite money, nonnegative quantities/fees, and Unix-second timestamps.');
            if (tx.derivativeType === 'Future' || tx.entityType === 'FuturesProduct' || tx.entityType === 'AssetIndex')
                throw new Error('Flat-rate portfolio taxes do not support futures or index-option tax treatment.');
            if (['exercise', 'assignment', 'assignmentBuy'].includes(tx.type) ||
                tx.exitReason === 'optionExpiryAutoBuy' ||
                tx.exitReason === 'optionExpiryAutoSell')
                throw new Error('Flat-rate portfolio taxes require option closes or worthless expirations; physical exercise/assignment is unsupported.');
            if (tx.type === 'expiration' && tx.value !== 0)
                throw new Error('Flat-rate option expirations must have zero settlement value.');
        }
    }
    eventTimestamps(start, end) {
        const result = [];
        for (let year = yearAt(start); year <= yearAt(end); year++) {
            for (const date of [`${year}-01-01 00:00:00`, `${year}-04-15 00:00:00`]) {
                const timestamp = getUnixTime(date);
                if (timestamp >= start && timestamp <= end)
                    result.push(timestamp);
            }
        }
        return result;
    }
    advance(timestamp, availableCash) {
        if (timestamp < this.lastTimestamp)
            throw new Error('Portfolio tax clock moved backwards.');
        this.lastTimestamp = timestamp;
        while (this.currentYear < yearAt(timestamp)) {
            const year = this.current();
            year.isFinalized = true;
            this.years.set(this.currentYear, year);
            this.carryforward = new Money(year.lossCarryforwardOut);
            this.currentYear++;
            this.realizedGain = new Money(0);
            this.ordinaryIncome = new Money(0);
        }
        let paid = new Money(0);
        for (const year of this.years.values()) {
            if (year.paymentTimestamp > timestamp)
                continue;
            const federal = new Money(year.federalTax).minus(year.federalTaxPaid);
            const state = new Money(year.stateTax).minus(year.stateTaxPaid);
            const due = federal.plus(state);
            if (due.lte(0))
                continue;
            if (paid.plus(due).gt(new Money(availableCash).plus('0.0000001')))
                throw new Error(`Insufficient unreserved cash for April 15 tax payment for ${year.taxYear}: $${due.toFixed(2)} due.`);
            paid = paid.plus(due);
            year.federalTaxPaid = year.federalTax;
            year.stateTaxPaid = year.stateTax;
        }
        return cents(paid);
    }
    processTransaction(tx) {
        PortfolioTaxAccount.validateTransactions([tx]);
        if (yearAt(tx.timestamp) !== this.currentYear)
            throw new Error('Advance the tax calendar before recording a transaction.');
        const key = `${tx.backtestTradeId}:${tx.legId}`;
        const cash = new Money(tx.value)
            .abs()
            .times(['buy', 'buyToOpen', 'buyToClose', 'buyToCover'].includes(tx.type) ? -1 : tx.type === 'expiration' ? 0 : 1)
            .minus(tx.fees);
        if (openingTypes.has(tx.type)) {
            if (this.lots.has(key) || !(tx.quantity > 0))
                throw new Error(`Invalid tax lot opening ${key}.`);
            this.lots.set(key, { quantity: new Money(tx.quantity), openingCash: cash });
        }
        else if (closingTypes.has(tx.type)) {
            const lot = this.lots.get(key);
            if (!lot)
                throw new Error(`Missing portfolio tax basis for ${key}.`);
            const quantity = tx.type === 'expiration' && tx.quantity === 0 ? lot.quantity : new Money(tx.quantity);
            const tolerance = lot.quantity.times('0.000000000000001');
            if (quantity.lte(0) || quantity.minus(lot.quantity).gt(tolerance))
                throw new Error(`Invalid tax lot close ${key}.`);
            const closed = quantity.minus(lot.quantity).abs().lte(tolerance) ? lot.quantity : quantity;
            const basis = lot.openingCash.times(closed).div(lot.quantity);
            this.realizedGain = this.realizedGain.plus(cash).plus(basis);
            lot.openingCash = lot.openingCash.minus(basis);
            lot.quantity = lot.quantity.minus(closed);
            if (lot.quantity.isZero())
                this.lots.delete(key);
        }
        else if (tx.type === 'splitIn') {
            const lot = this.lots.get(key);
            if (!lot || !(tx.quantity > 0) || tx.value !== 0 || tx.fees !== 0)
                throw new Error(`Invalid tax lot split ${key}.`);
            lot.quantity = new Money(tx.quantity);
        }
        else if (tx.type === 'splitOut') {
            if (tx.value !== 0 || tx.fees !== 0 || !this.lots.has(key))
                throw new Error(`Invalid tax lot split ${key}.`);
        }
        else if (tx.type === 'dividend' || tx.type === 'dividendReinvestment') {
            this.ordinaryIncome = this.ordinaryIncome.plus(cash);
        }
        else
            throw new Error(`Unsupported portfolio tax transaction ${tx.type}.`);
    }
    current() {
        const gain = this.realizedGain.minus(this.carryforward);
        const taxableGain = Decimal.max(0, gain);
        const taxableIncome = taxableGain.plus(Decimal.max(0, this.ordinaryIncome));
        return {
            taxYear: this.currentYear,
            realizedGain: cents(this.realizedGain),
            ordinaryIncome: cents(this.ordinaryIncome),
            lossCarryforwardIn: cents(this.carryforward),
            lossCarryforwardOut: cents(Decimal.max(0, gain.negated())),
            taxableGain: cents(taxableGain),
            federalTax: cents(taxableIncome.times(this.configuration.federalRatePercent).div(100)),
            stateTax: cents(taxableIncome.times(this.configuration.stateRatePercent).div(100)),
            paymentTimestamp: getUnixTime(`${this.currentYear + 1}-04-15 00:00:00`),
            federalTaxPaid: 0,
            stateTaxPaid: 0,
            isFinalized: false
        };
    }
    get reservedCash() {
        return cents([...this.years.values()].reduce((sum, year) => sum.plus(year.federalTax).plus(year.stateTax).minus(year.federalTaxPaid).minus(year.stateTaxPaid), new Money(0)));
    }
    get unpaidTaxes() {
        const current = this.current();
        return cents(new Money(this.reservedCash).plus(current.federalTax).plus(current.stateTax));
    }
    get taxesPaid() {
        return cents([...this.years.values()].reduce((sum, year) => sum.plus(year.federalTaxPaid).plus(year.stateTaxPaid), new Money(0)));
    }
    snapshot() {
        return [...this.years.values()].map((year) => ({ ...year })).concat(this.current());
    }
    requireComplete() {
        if (this.lots.size)
            throw new Error('Portfolio tax accounting ended with unclosed tax lots.');
    }
}
//# sourceMappingURL=PortfolioTaxAccount.js.map